/**
 * **Run 17 item B4 (DECIDED 20261015, Barna): the live check against the
 * recommendation.**
 *
 * Two lines above the dish list, one per side, which move on every change the
 * item names — − / +, the headcount, the appetite, the balance, *Vissza az
 * ajánlotthoz* and *Visszavonás*:
 *
 *     Sós: 6,1 falat/fő · ajánlott: 6,1 — megfelelő
 *     Édes: 2,4 falat/fő · ajánlott: 3,0 — kevesebb az ajánlottnál
 *
 * **What the two figures are.** Both are measured the same way, in made
 * pieces per guest on that side:
 *
 *   * `current`     — what the menu holds now, ÷ the headcount.
 *   * `recommended` — what the same engine makes of the same menu with **no**
 *     customer quantity on any dish, at the headcount, occasion, duration,
 *     appetite and balance in force now, ÷ the headcount. That is the line's
 *     own `recommendedPortions`, the figure *Vissza az ajánlotthoz* restores
 *     to, summed over the side.
 *
 * Measuring both the same way is the whole point, and it is a reading worth
 * stating (assumption A1). The §6.1 figure the engine starts from is pieces
 * per guest **before** the production margin; what is made carries the margin
 * of §6.4, 6 to 25 % by headcount band. Comparing the made figure against the
 * §6.1 one would put an untouched menu — one nobody has changed — over the
 * band at most headcounts and read *több az ajánlottnál*, and it would put a
 * production margin on the page as a number, which §5.11 does not allow. So
 * an untouched menu reads *megfelelő* exactly, and the line moves when, and
 * only when, the customer moves a quantity.
 *
 * **Drinks are excluded**, and so is fruit: both sit outside pieces per guest
 * (§5.6). Only the savoury and sweet sides are measured.
 *
 * **The band** is `menu_check_band`, 0,10 — held in the register, never a
 * literal here. Exactly ±10 % is *megfelelő*; the comparison is done in whole
 * pieces scaled by ten thousand rather than in floating point, so the
 * boundary is the same answer every time.
 */

import { fromDecimal, toNumber, type Side } from '@plt/engine'

import type { PricedPage } from './model.ts'

/** The three states item B4 names. Each is shown as text **and** an icon. */
export type MenuCheckState = 'ok' | 'below' | 'above'

export interface SideCheck {
  side: Side
  /** Pieces of this side in the menu now, per guest. */
  current: number
  /** What the engine recommends for the same menu, per guest. */
  recommended: number
  state: MenuCheckState
}

export interface MenuCheck {
  sav: SideCheck
  sw: SideCheck
}

/** §12 `menu_check_band`, as a proportion. Falls back to nothing being read. */
export const MENU_CHECK_BAND_PARAM = 'menu_check_band'

/**
 * The band, out of the register. 12 holds every value as text and the
 * catalogue's own Hungarian writes a decimal comma, so it is read through
 * the engine's own reader rather than through `Number`, which answers NaN
 * for `0,10`. An empty or unreadable value is **nothing**, so any difference
 * at all reads as above or below rather than silently as right.
 */
export function menuCheckBandOf(param: (key: string) => string): number {
  const raw = String(param(MENU_CHECK_BAND_PARAM) ?? '').trim()
  if (raw === '') return 0
  try {
    const value = toNumber(fromDecimal(raw))
    return Number.isFinite(value) && value >= 0 ? value : 0
  } catch {
    return 0
  }
}

/** The scale the boundary is compared on, so ±10 % is exact. */
const SCALE = 10_000

export function sideState(current: number, recommended: number, band: number): MenuCheckState {
  const edge = Math.round(Math.max(band, 0) * SCALE)
  const madeScaled = Math.round(current * SCALE)
  const wantScaled = Math.round(recommended * SCALE)
  if (wantScaled === 0) return madeScaled === 0 ? 'ok' : 'above'
  const gap = (madeScaled - wantScaled) * SCALE
  const allowed = edge * wantScaled
  if (gap > allowed) return 'above'
  if (-gap > allowed) return 'below'
  return 'ok'
}

export function menuCheckOf(priced: PricedPage, band: number): MenuCheck {
  const guests = Math.max(priced.guests, 1)
  const one = (side: Side): SideCheck => {
    const allocation = priced.priced.sides[side]
    const made = allocation.all.reduce((sum, line) => sum + line.portions, 0)
    const want = allocation.all.reduce((sum, line) => sum + line.recommendedPortions, 0)
    const current = made / guests
    const recommended = want / guests
    return { side, current, recommended, state: sideState(current, recommended, band) }
  }
  return { sav: one('sav'), sw: one('sw') }
}

/**
 * The state, as the customer reads it: a word and an icon, never colour
 * alone. The icons are plain characters rather than coloured shapes, so a
 * screen reader reads the word and a monochrome print still carries both
 * (assumption A2).
 */
export const MENU_CHECK_STATE_TEXT: Readonly<Record<MenuCheckState, { hu: string; en: string }>> = {
  ok: { hu: 'megfelelő', en: 'about right' },
  below: { hu: 'kevesebb az ajánlottnál', en: 'below the recommendation' },
  above: { hu: 'több az ajánlottnál', en: 'above the recommendation' },
}

export const MENU_CHECK_STATE_ICON: Readonly<Record<MenuCheckState, string>> = {
  ok: '✓',
  below: '↓',
  above: '↑',
}
